import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { PRODUCTS } from '../../data/products';

const CartDrawer = ({ isOpen, onClose, cart }) => {
  const [shouldRender, setShouldRender] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      const t = setTimeout(() => setIsVisible(true), 50);
      return () => clearTimeout(t);
    } else {
      setIsVisible(false);
      const t = setTimeout(() => setShouldRender(false), 400);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  const handleCheckout = async () => {
    if (cart.cartItems.length === 0) return;
    setCheckoutLoading(true);
    try {
      const res = await fetch('/api/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: cart.cartItems }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert(data.error || 'Checkout failed');
      }
    } catch {
      alert('Could not connect to checkout. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (!shouldRender) return null;

  const content = (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.6)',
          zIndex: 9998,
          opacity: isVisible ? 1 : 0,
          transition: 'opacity 0.3s ease',
        }}
      />
      {/* Drawer */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          maxWidth: 420,
          background: '#0a0a0a',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          fontFamily: 'Montserrat, sans-serif',
          transform: isVisible ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.35s ease',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '24px 24px 20px',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          <h2
            style={{
              fontWeight: 300,
              fontSize: 18,
              color: '#ffffff',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              margin: 0,
            }}
          >
            Cart
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#ffffff',
              fontSize: 28,
              cursor: 'pointer',
              fontWeight: 300,
              lineHeight: 1,
              padding: 0,
            }}
            aria-label="Close cart"
          >
            ×
          </button>
        </div>

        {/* Items */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
          {cart.cartItems.length === 0 ? (
            <p
              style={{
                color: 'rgba(255,255,255,0.4)',
                fontWeight: 300,
                fontSize: 14,
                textAlign: 'center',
                marginTop: 48,
              }}
            >
              Your cart is empty
            </p>
          ) : (
            cart.cartItems.map((item) => {
              const product = PRODUCTS.find((p) => p.id === item.productId);
              if (!product) return null;
              return (
                <div
                  key={item.productId}
                  style={{
                    display: 'flex',
                    gap: 16,
                    padding: '16px 0',
                    borderBottom: '1px solid rgba(255,255,255,0.08)',
                  }}
                >
                  <img
                    src={product.image}
                    alt={product.title}
                    style={{
                      width: 72,
                      height: 72,
                      objectFit: 'cover',
                      borderRadius: 2,
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p
                      style={{
                        fontWeight: 500,
                        fontSize: 13,
                        color: '#ffffff',
                        letterSpacing: '0.05em',
                        margin: 0,
                        textTransform: 'uppercase',
                      }}
                    >
                      {product.title}
                    </p>
                    <p
                      style={{
                        fontWeight: 300,
                        fontSize: 13,
                        color: 'rgba(255,255,255,0.5)',
                        margin: '4px 0 10px',
                      }}
                    >
                      ${product.price.toFixed(2)}
                    </p>
                    {/* Quantity controls */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <button
                        onClick={() => cart.updateQuantity(item.productId, item.quantity - 1)}
                        style={qtyBtnStyle}
                      >
                        −
                      </button>
                      <span
                        style={{
                          fontWeight: 400,
                          fontSize: 13,
                          color: '#ffffff',
                          minWidth: 20,
                          textAlign: 'center',
                        }}
                      >
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => cart.updateQuantity(item.productId, item.quantity + 1)}
                        style={qtyBtnStyle}
                      >
                        +
                      </button>
                      <button
                        onClick={() => cart.removeFromCart(item.productId)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'rgba(255,255,255,0.3)',
                          fontSize: 12,
                          cursor: 'pointer',
                          fontFamily: 'Montserrat, sans-serif',
                          letterSpacing: '0.08em',
                          marginLeft: 'auto',
                          padding: 0,
                        }}
                      >
                        REMOVE
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        {cart.cartItems.length > 0 && (
          <div
            style={{
              padding: '20px 24px 28px',
              borderTop: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: 20,
              }}
            >
              <span style={{ fontWeight: 300, fontSize: 14, color: '#ffffff', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                Total
              </span>
              <span style={{ fontWeight: 500, fontSize: 16, color: '#ffffff' }}>
                ${cart.cartTotal.toFixed(2)}
              </span>
            </div>
            <button
              onClick={handleCheckout}
              disabled={checkoutLoading}
              style={{
                width: '100%',
                padding: '14px 0',
                background: checkoutLoading ? '#555' : '#36454F',
                color: '#ffffff',
                border: 'none',
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 500,
                fontSize: 14,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                cursor: checkoutLoading ? 'not-allowed' : 'pointer',
                borderRadius: 2,
                transition: 'background 0.3s ease',
              }}
              onMouseEnter={(e) => {
                if (!checkoutLoading) {
                  e.currentTarget.style.background = '#ffffff';
                  e.currentTarget.style.color = '#000000';
                }
              }}
              onMouseLeave={(e) => {
                if (!checkoutLoading) {
                  e.currentTarget.style.background = '#36454F';
                  e.currentTarget.style.color = '#ffffff';
                }
              }}
            >
              {checkoutLoading ? 'Processing...' : 'Checkout'}
            </button>
          </div>
        )}
      </div>
    </>
  );

  return ReactDOM.createPortal(content, document.body);
};

const qtyBtnStyle = {
  width: 28,
  height: 28,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'none',
  border: '1px solid rgba(255,255,255,0.2)',
  color: '#ffffff',
  fontSize: 16,
  cursor: 'pointer',
  borderRadius: 2,
  fontFamily: 'Montserrat, sans-serif',
  padding: 0,
};

export default CartDrawer;
